import {
  ArrowDropDown as ArrowDropDownIcon,
  DeleteOutline as DeleteIcon,
  ViewWeekOutlined as LayoutIcon,
} from "@mui/icons-material";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  ListItemText,
  Menu,
  MenuItem,
  TextField,
  Tooltip,
} from "@mui/material";
import Button from "components/common/Button";
import { useState } from "react";

// Per-user, browser-local saved views for the Review tab. Lets a user keep
// several named layouts (columns/filters/sort/grouping/summary) and switch
// between them. "Default view" = the company config default.
const LayoutMenu = ({
  activeName,
  names,
  hasChanges,
  onSelect,
  onSaveAs,
  onDelete,
  container,
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nameInput, setNameInput] = useState("");
  const open = Boolean(anchorEl);

  const closeMenu = () => setAnchorEl(null);
  const label = activeName || "Default view";

  const openSaveDialog = () => {
    setNameInput(activeName ?? "");
    setDialogOpen(true);
    closeMenu();
  };

  const confirmSave = () => {
    const name = nameInput.trim();
    if (!name) return;
    onSaveAs(name);
    setDialogOpen(false);
    setNameInput("");
  };

  const trimmed = nameInput.trim();

  return (
    <>
      <Button
        size={"small"}
        color={"secondary"}
        startIcon={<LayoutIcon fontSize={"small"} />}
        endIcon={<ArrowDropDownIcon fontSize={"small"} />}
        onClick={(e) => setAnchorEl(e.currentTarget)}
        tooltip={"Saved views"}
        tooltipRequired
        sx={{ whiteSpace: "nowrap", maxWidth: 240 }}
      >
        {`${label}${hasChanges ? " •" : ""}`}
      </Button>
      <Menu
        open={open}
        anchorEl={anchorEl}
        onClose={closeMenu}
        container={container}
      >
        <MenuItem
          selected={!activeName}
          onClick={() => {
            onSelect(null);
            closeMenu();
          }}
        >
          {"Default view"}
        </MenuItem>
        {names.length > 0 && <Divider />}
        {names.map((name) => (
          <MenuItem
            key={name}
            selected={name === activeName}
            onClick={() => {
              onSelect(name);
              closeMenu();
            }}
            sx={{ display: "flex", gap: 2, pr: 1 }}
          >
            <ListItemText sx={{ mr: 2 }}>{name}</ListItemText>
            <Tooltip title={"Delete this view"}>
              <IconButton
                size={"small"}
                edge={"end"}
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete(name);
                }}
              >
                <DeleteIcon fontSize={"small"} />
              </IconButton>
            </Tooltip>
          </MenuItem>
        ))}
        <Divider />
        {activeName && (
          <MenuItem
            disabled={!hasChanges}
            onClick={() => {
              onSaveAs(activeName);
              closeMenu();
            }}
          >
            {`Update "${activeName}"`}
          </MenuItem>
        )}
        <MenuItem onClick={openSaveDialog}>{"Save current as…"}</MenuItem>
      </Menu>

      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth={"xs"}
        fullWidth
        container={container}
      >
        <DialogTitle>{"Save view"}</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            fullWidth
            label={"View name"}
            value={nameInput}
            onChange={(e) => setNameInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") confirmSave();
            }}
            helperText={
              trimmed && names.includes(trimmed)
                ? `Overwrites the existing "${trimmed}" view`
                : " "
            }
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button color={"secondary"} onClick={() => setDialogOpen(false)}>
            {"Cancel"}
          </Button>
          <Button onClick={confirmSave} isDisabled={!trimmed}>
            {"Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default LayoutMenu;
